import { useCallback, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { auth } from "@/integrations/firebase/auth";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import { findActiveRequestId } from "./impersonationStop";

export interface UseImpersonationResult {
  start: (targetUid: string) => Promise<void>;
  stop: () => Promise<void>;
  loading: boolean;
  error: Error | null;
}

const REQUEST_TIMEOUT_MS = 30_000;

/** Wait until the request doc reaches `doneStatus`; reject on FAILED/timeout. */
function waitForStatus(requestId: string, doneStatus: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let unsubscribe: Unsubscribe | undefined;
    const finish = (err?: Error) => {
      clearTimeout(timer);
      unsubscribe?.();
      if (err) reject(err);
      else resolve();
    };
    const timer = setTimeout(
      () => finish(new Error("Impersonation request timed out.")),
      REQUEST_TIMEOUT_MS,
    );
    unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.IMPERSONATION_REQUESTS, requestId),
      (snapshot) => {
        const status = snapshot.data()?.status as string | undefined;
        if (status === doneStatus) {
          finish();
        } else if (status === "FAILED") {
          finish(
            new Error(
              (snapshot.data()?.error as string | undefined) ??
                "Impersonation request failed.",
            ),
          );
        }
      },
      (err) => finish(err),
    );
  });
}

/**
 * Start/stop impersonation (SUPER_ADMIN only). The client writes a
 * rules-guarded impersonation_requests doc; the on_impersonation_requested
 * trigger sets or clears the `impersonating` custom claim on the caller and
 * advances the doc status. Once the doc reaches ACTIVE/STOPPED the ID token
 * is force-refreshed so `onIdTokenChanged` (in AuthContext) picks up the new
 * claim and swaps the active profile.
 */
export function useImpersonation(): UseImpersonationResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const activeRequestIdRef = useRef<string | null>(null);

  const run = useCallback(async (fn: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
      await auth.currentUser?.getIdToken(true);
    } catch (err) {
      logError(err as Error);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deliberately the one pair of writes with no `assertWritable()`: starting
  // and stopping a session are how the guard is turned on and off, and
  // guarding stop would strand the admin inside an identity they cannot leave
  // for four hours. Everything else that writes calls the choke point — if
  // you are adding a mutation, it is not this exception.
  const start = useCallback(
    (targetUid: string) =>
      run(async () => {
        const adminUid = auth.currentUser?.uid;
        if (!adminUid) throw new Error("Not authenticated.");
        const requestRef = await addDoc(
          collection(db, COLLECTIONS.IMPERSONATION_REQUESTS),
          {
            adminId: adminUid,
            targetUid,
            status: "REQUESTED",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        );
        activeRequestIdRef.current = requestRef.id;
        await waitForStatus(requestRef.id, "ACTIVE");
      }),
    [run],
  );

  const stop = useCallback(
    () =>
      run(async () => {
        const adminUid = auth.currentUser?.uid;
        if (!adminUid) throw new Error("Not authenticated.");
        const requestId =
          activeRequestIdRef.current ?? (await findActiveRequestId(adminUid));
        if (!requestId) {
          throw new Error("No active impersonation request found.");
        }
        await updateDoc(
          doc(db, COLLECTIONS.IMPERSONATION_REQUESTS, requestId),
          {
            status: "STOP_REQUESTED",
            updatedAt: serverTimestamp(),
          },
        );
        await waitForStatus(requestId, "STOPPED");
        activeRequestIdRef.current = null;
      }),
    [run],
  );

  return { start, stop, loading, error };
}
